import { spErrorTags } from "@eventr/common";
import { NativeNavigation } from "@eventr/native/navigation";
import { isEqual } from "lodash";
import { isIosNativeNavigation } from "feat/nativeNavigation/platform";
import { useBoundStore } from "state/bound.store";
import { DraftSectionItems, DraftStagedAt, pruneExpiredDraftSectionItems } from "state/itineraryState/draft-ttl";
import { flushStoredWrites, indexDbStorage } from "state/indexdbStorage";
import { useStorageStore } from "state/storage/storage.store";

/** Pulls the persisted draft keys into this realm's store once it becomes visible. */
export async function pullLatestDrafts(): Promise<void> {
  if (useStorageStore.getState().hasHydrated !== "finished") return;
  try {
    // Land this realm's pending write first, so a pull cannot replace newer in-memory drafts
    // with the stale disk value and then persist that stale value over the write.
    await flushStoredWrites();
    const raw = await indexDbStorage.getItem("bound-store");
    if (!raw) return;
    const persisted = (JSON.parse(raw) as {
      state?: { draftSectionItems?: DraftSectionItems; draftSectionItemStagedAt?: DraftStagedAt };
    })?.state;
    const drafts = persisted?.draftSectionItems;
    if (!drafts) return;
    const pruned = pruneExpiredDraftSectionItems(drafts, persisted?.draftSectionItemStagedAt);
    const current = useBoundStore.getState();
    // A no-op re-set would re-fire draft subscribers for nothing.
    if (isEqual(pruned.drafts, current.draftSectionItems) && isEqual(pruned.stagedAt, current.draftSectionItemStagedAt))
      return;
    useBoundStore.setState(
      { draftSectionItems: pruned.drafts, draftSectionItemStagedAt: pruned.stagedAt },
      false,
      "pullLatestDrafts",
    );
  } catch (error) {
    spErrorTags("pullLatestDrafts failed", ["DRAFTS"], error);
  }
}

// One registration per realm. A pushed screen's own view events are the only cross-webview
// handoff signals it gets.
const flushPendingWrites = () => {
  void flushStoredWrites();
};

// A pushed webview's URL is /capacitor-native-navigation/<viewId>. The listener names below
// are component-scoped, `type:componentId`, matching @eventr/native's own listeners.
const ownViewId = window.location.pathname.match(/^\/capacitor-native-navigation\/([a-z0-9-]+)\/?$/)?.[1];

if (isIosNativeNavigation() && ownViewId) {
  void NativeNavigation.addListener(`viewDidDisappear:${ownViewId}`, flushPendingWrites).catch((error) =>
    spErrorTags("viewDidDisappear listener", ["DRAFTS"], error),
  );
  void NativeNavigation.addListener(`viewDidAppear:${ownViewId}`, () => void pullLatestDrafts()).catch((error) =>
    spErrorTags("viewDidAppear listener", ["DRAFTS"], error),
  );
}

// DOM backstop in every realm; the root webview gets no native view events of its own.
document.addEventListener("visibilitychange", () =>
  document.visibilityState === "hidden" ? flushPendingWrites() : void pullLatestDrafts(),
);
window.addEventListener("pagehide", flushPendingWrites);
window.addEventListener("pageshow", () => void pullLatestDrafts());
